import { Fragment, useState } from "react";
import { CAPABILITY_LABEL, type AiCapability, type AiProviderId } from "@gk/shared";
import { api } from "../../lib/api";
import { fmtDate, useAsync } from "../../lib/hooks";
import { ErrorNote } from "../../components/ui";
import { AdminTabs, VendorChip } from "./common";

interface Health {
  environment: string;
  role: string;
  uptimeSeconds: number;
  database: { ok: boolean; latencyMs: number };
  jobs: Record<string, number> | null;
  failedJobs24h: number | null;
  ai: Array<{ provider: AiProviderId; capability: AiCapability; ok: boolean; reason?: string }>;
  auditLastVerified: { at: string; intact: boolean } | null;
  clients: { active: number; suspended: number };
  mfaRequired: boolean;
  authMode: string;
}

interface AuditRow {
  id: number;
  at: string;
  action: string;
  targetType: string | null;
  targetId: string | null;
  actorKind: string;
  actorEmail: string | null;
  clientName: string | null;
  ip: string | null;
  meta: Record<string, unknown>;
}

export function AdminSecurityPage() {
  const health = useAsync(() => api.get<Health>("/api/admin/health"), []);
  const clients = useAsync(() => api.get<Array<{ id: string; name: string }>>("/api/admin/clients"), []);
  const [filters, setFilters] = useState({ tenantId: "", action: "", actor: "" });
  const [applied, setApplied] = useState(filters);
  const [rows, setRows] = useState<AuditRow[]>([]);
  const qs = (before?: number) =>
    new URLSearchParams(Object.entries({ ...applied, ...(before ? { before: String(before) } : {}) }).filter(([, v]) => v) as Array<[string, string]>).toString();
  const audit = useAsync(async () => {
    const r = await api.get<AuditRow[]>(`/api/admin/audit?${qs()}`);
    setRows(r);
    return r;
  }, [applied]);
  const [verify, setVerify] = useState<{ intact: boolean; events: number; firstBrokenId: number | null; ms: number } | "running" | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [open, setOpen] = useState<number | null>(null);

  const runVerify = async () => {
    setVerify("running");
    setError(null);
    try {
      setVerify(await api.post("/api/admin/audit/verify"));
      await health.reload();
    } catch (err) {
      setError(err as Error);
      setVerify(null);
    }
  };
  const older = async () => {
    const last = rows[rows.length - 1];
    if (!last) return;
    const more = await api.get<AuditRow[]>(`/api/admin/audit?${qs(last.id)}`);
    setRows([...rows, ...more]);
  };
  const h = health.data;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Admin panel</h1>
          <p className="sub">System health and the tamper-evident record of every sign-in-driven action. The audit trail is append-only and hash-chained.</p>
        </div>
      </div>
      <AdminTabs />
      <div className="stack" style={{ gap: 20 }}>
        <ErrorNote error={error ?? health.error} />
        {h && (
          <div className="health-grid">
            <div className={`health ${h.database.ok ? "ok" : "bad"}`}>
              <span>Database</span>
              <b>{h.database.ok ? `Responding in ${h.database.latencyMs} ms` : "Not responding"}</b>
            </div>
            <div className={`health ${(h.failedJobs24h ?? 0) > 0 ? "warn" : "ok"}`}>
              <span>Background work, last 24 hours</span>
              <b>{h.failedJobs24h == null ? "Runs in-process" : `${h.failedJobs24h} failed`}</b>
              {h.jobs && <small>{Object.entries(h.jobs).map(([k, n]) => `${k} ${n}`).join(" · ")}</small>}
            </div>
            <div className={`health ${h.auditLastVerified?.intact === false ? "bad" : h.auditLastVerified ? "ok" : "warn"}`}>
              <span>Audit trail</span>
              <b>{h.auditLastVerified ? (h.auditLastVerified.intact ? "Intact" : "Chain broken — investigate") : "Not checked from here yet"}</b>
              {h.auditLastVerified && <small>Checked {fmtDate(h.auditLastVerified.at)}; also checked daily by a scheduled job</small>}
            </div>
            <div className={`health ${h.mfaRequired ? "ok" : "warn"}`}>
              <span>Sign-in</span>
              <b>{h.mfaRequired ? "Two-step verification required" : "Two-step verification not enforced"}</b>
              <small>{h.authMode === "dev" ? "Development sign-in (never in production)" : "Identity Platform"}</small>
            </div>
            <div className="health ok">
              <span>Clients</span>
              <b>
                {h.clients.active} active{h.clients.suspended ? `, ${h.clients.suspended} suspended` : ""}
              </b>
              <small>
                {h.environment} · running {Math.round(h.uptimeSeconds / 3600)} h
              </small>
            </div>
            <div className={`health ${h.ai.every((a) => a.ok) ? "ok" : "bad"}`}>
              <span>AI providers in use</span>
              <div className="stack" style={{ gap: 4, marginTop: 4 }}>
                {h.ai.map((a, i) => (
                  <div key={i} className="row small" style={{ gap: 6 }}>
                    <VendorChip id={a.provider} /> {CAPABILITY_LABEL[a.capability]} {a.ok ? "" : <b className="bad-text">— {a.reason}</b>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <section className="panel row wrap" style={{ gap: 16 }}>
          <div className="stack" style={{ gap: 4 }}>
            <h3>Verify the audit trail</h3>
            <p className="small muted">Recomputes every link of the hash chain. Any edited or deleted entry breaks it.</p>
          </div>
          <span className="spacer" />
          {verify && verify !== "running" && (
            <span className={`pill ${verify.intact ? "green" : "red"}`}>
              {verify.intact ? `Intact: ${verify.events.toLocaleString()} events in ${verify.ms} ms` : `Broken at event ${verify.firstBrokenId}`}
            </span>
          )}
          <button className="btn" onClick={runVerify} disabled={verify === "running"}>
            {verify === "running" ? "Checking…" : "Verify now"}
          </button>
        </section>

        <section className="panel flush" aria-labelledby="audit-h">
          <div className="panel-head" style={{ padding: "16px 20px 0" }}>
            <h3 id="audit-h">Audit trail</h3>
          </div>
          <form
            className="row wrap"
            style={{ padding: "0 20px 14px", gap: 10 }}
            onSubmit={(e) => {
              e.preventDefault();
              setApplied(filters);
            }}
          >
            <select className="input compact" aria-label="Client" value={filters.tenantId} onChange={(e) => setFilters({ ...filters, tenantId: e.target.value })}>
              <option value="">All clients and platform</option>
              {(clients.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <input className="input compact" placeholder="Action starts with, e.g. ai. or member." value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} aria-label="Action" />
            <input className="input compact" placeholder="Person's email" value={filters.actor} onChange={(e) => setFilters({ ...filters, actor: e.target.value })} aria-label="Person" />
            <button className="btn small">Filter</button>
          </form>
          <ErrorNote error={audit.error} />
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Action</th>
                <th>Who</th>
                <th>Client</th>
                <th>Target</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Fragment key={r.id}>
                  <tr className="click" onClick={() => setOpen(open === r.id ? null : r.id)}>
                    <td className="small">{new Date(r.at).toLocaleString()}</td>
                    <td>
                      <code>{r.action}</code>
                    </td>
                    <td className="small">{r.actorEmail ?? r.actorKind}</td>
                    <td className="small">{r.clientName ?? <span className="faint">Platform</span>}</td>
                    <td className="xs muted">
                      {r.targetType} {r.targetId?.slice(0, 12)}
                    </td>
                  </tr>
                  {open === r.id && (
                    <tr>
                      <td colSpan={5}>
                        <pre className="meta">{JSON.stringify({ id: r.id, ip: r.ip, ...r.meta }, null, 2)}</pre>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
          {rows.length >= 100 && (
            <div className="row" style={{ padding: 14 }}>
              <span className="spacer" />
              <button className="btn small" onClick={older}>
                Load older
              </button>
            </div>
          )}
        </section>
      </div>
    </>
  );
}
